import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { User, MapPin, Package, Calendar, FileText, Loader, AlertCircle } from 'lucide-react';
import {
  OptimizedFormSection,
  OptimizedFormGrid,
  SectionTitle,
  FieldContainer,
  CategoryBadge
} from './OptimizedFormComponents';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../../styles/ThemeStyles';
import { CustomerDataService } from './CustomerDataService';
import { useGlobalOrderData } from '../../../hooks/useGlobalOrderData';

// Shared interfaces
export interface SharedOrderFormData {
  // Customer Information (Autopopulated from database)
  customerNumber: string;
  storeName: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  email: string;
  phoneNumber: string;
  
  // Order Information
  poNumber: string;
  orderDate: string;
  deliveryDate: string;
  orderType: 'custOrder' | 'offCarDelivery' | null;
  specialInstructions: string;
}

// Autopopulated Field Component
export const AutopopulatedField: React.FC<{
  label: string;
  value: string;
  icon?: React.ComponentType<any>;
  type?: 'text' | 'email' | 'tel';
}> = ({ label, value, icon: Icon, type = 'text' }) => (
  <FieldContainer label={label}>
    <div className="field-input">
      {Icon && <Icon className="input-icon" size={18} />}
      <input
        className="input-field autopopulated"
        type={type}
        value={value}
        readOnly
        disabled
      />
    </div>
  </FieldContainer>
);

// Styled component for Cust Order notice
const CustOrderNotice = styled.div`
  padding: ${Spacing.md};
  background-color: #FFEB3B; /* Yellow background */
  color: #000000; /* Black text */
  border: 1px solid #FBC02D;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  margin-top: ${Spacing.sm};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

// Radio Button Group Component (supports both boolean and string values)
export const RadioButtonGroup: React.FC<{
  label: string;
  name: string;
  value: boolean | string | null;
  onChange: (value: boolean | string) => void;
  options: { label: string; value: boolean | string }[];
  required?: boolean;
  error?: string;
  showCustOrderNotice?: boolean; // New prop to show notice for Cust Order
}> = ({ label, name, value, onChange, options, required, error, showCustOrderNotice = false }) => {
  const { orderData } = useGlobalOrderData();
  
  // Calculate total items for store orders (exclude supplies)
  const totalItems = React.useMemo(() => {
    const items = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [orderData.heritageItems, orderData.livingstonItems, orderData.flowerItems, orderData.vegetableItems]);
  
  // Show notice when Cust Order is selected
  const showNotice = showCustOrderNotice && value === 'custOrder';
  
  return (
    <FieldContainer label={label} required={required} error={error}>
      <div className="radio-group">
        {options.map((option) => (
          <label key={option.label} className="radio-option">
            <input
              type="radio"
              name={name}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className="radio-label">{option.label}</span>
          </label>
        ))}
      </div>
      {showNotice && (
        <CustOrderNotice>
          <AlertCircle size={18} />
          <span>
            {totalItems === 0
              ? 'Cust Order requires a minimum of 150 items in total quantity (across all 4 store order forms)'
              : totalItems < 150
              ? `Cust Order requires a minimum of 150 items. Current total: ${totalItems} items (${150 - totalItems} more needed)`
              : `Cust Order requires a minimum of 150 items. Current total: ${totalItems} items ✓`
            }
          </span>
        </CustOrderNotice>
      )}
    </FieldContainer>
  );
};

// Styled components for radio buttons
const RadioGroup = styled.div`
  display: flex;
  gap: 30px;
  margin-top: ${Spacing.sm};
  
  .radio-option {
    display: flex;
    align-items: center;
    gap: ${Spacing.sm};
    cursor: pointer;
    
    input[type="radio"] {
      width: 18px;
      height: 18px;
      accent-color: ${ThemeColors.primary.main};
    }
    
    .radio-label {
      font-size: ${Typography.fontSize.md};
      color: ${ThemeColors.text.primary};
    }
  }
`;

// Customer Information Section Component
export const CustomerInformationSection: React.FC<{
  data: SharedOrderFormData;
  onChange: (field: keyof SharedOrderFormData, value: string) => void;
  errors?: { [key: string]: string };
  loading?: boolean;
  error?: string | null;
}> = ({ data, onChange, errors = {}, loading = false, error = null }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <OptimizedFormSection>
        <SectionTitle 
          icon={User} 
          title={t('forms.customerInformation')} 
          subtitle={t('forms.loadingCustomerDetails')}
        />
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: Spacing.xl,
          color: ThemeColors.text.secondary 
        }}>
          <Loader className="animate-spin" size={24} />
          <span style={{ marginLeft: Spacing.md }}>{t('forms.loadingCustomerData')}</span>
        </div>
      </OptimizedFormSection>
    );
  }

  if (error) {
    return (
      <OptimizedFormSection>
        <SectionTitle 
          icon={User} 
          title={t('forms.customerInformation')} 
          subtitle={t('forms.errorLoadingCustomer')}
        />
        
        <div style={{ 
          padding: Spacing.lg,
          backgroundColor: ThemeColors.error.light,
          border: `1px solid ${ThemeColors.error.main}`,
          borderRadius: Spacing.sm,
          color: ThemeColors.error.main
        }}>
          <strong>{t('common.error')}:</strong> {error}
          <div style={{ marginTop: Spacing.sm, fontSize: Typography.fontSize.sm }}>
            {t('forms.usingFallbackData')}
          </div>
        </div>
      </OptimizedFormSection>
    );
  }

  return (
    <OptimizedFormSection>
      <SectionTitle 
        icon={User} 
        title={t('forms.customerInformation')}
      />
      
      <OptimizedFormGrid>
        <AutopopulatedField
          label={t('forms.customerHash')}
          value={data.customerNumber}
          icon={User}
        />
        
        <AutopopulatedField
          label={t('forms.storeName')}
          value={data.storeName}
          icon={User}
        />
        
        <AutopopulatedField
          label={t('forms.address')}
          value={data.address}
          icon={MapPin}
        />
        
        <AutopopulatedField
          label={t('forms.city')}
          value={data.city}
          icon={MapPin}
        />
        
        <AutopopulatedField
          label={t('forms.province')}
          value={data.province}
          icon={MapPin}
        />
        
        <AutopopulatedField
          label={t('forms.postalCode')}
          value={data.postalCode}
          icon={MapPin}
        />
        
        <AutopopulatedField
          label={t('forms.email')}
          value={data.email}
          icon={User}
          type="email"
        />
        
        <AutopopulatedField
          label={t('forms.phoneNumber')}
          value={data.phoneNumber}
          icon={User}
          type="tel"
        />
      </OptimizedFormGrid>
    </OptimizedFormSection>
  );
};

// Order Information Section Component
export const OrderInformationSection: React.FC<{
  data: SharedOrderFormData;
  onChange: (field: keyof SharedOrderFormData, value: string | boolean) => void;
  errors?: { [key: string]: string };
  showCustOrderMinimumNotice?: boolean;
}> = ({ data, onChange, errors = {}, showCustOrderMinimumNotice = true }) => {
  const { t } = useTranslation();
  
  return (
    <OptimizedFormSection>
      <SectionTitle 
        icon={Package} 
        title={t('forms.orderInformation')} 
        subtitle={t('forms.enterOrderDetails', 'Enter your order details')}
      />
      
      <OptimizedFormGrid>
        <FieldContainer label={t('forms.poNumber')} error={errors.poNumber}>
          <input
            className="input-field"
            value={data.poNumber}
            onChange={(e) => onChange('poNumber', e.target.value)}
            placeholder={t('forms.enterPONumber')}
          />
        </FieldContainer>
        
        <FieldContainer label={t('forms.orderDate')} required error={errors.orderDate}>
          <div className="field-input">
            <Calendar className="input-icon" size={18} />
            <input
              className="input-field"
              type="date"
              value={data.orderDate}
              onChange={(e) => onChange('orderDate', e.target.value)}
            />
          </div>
        </FieldContainer>
        
        <FieldContainer label={t('forms.deliveryDate')} error={errors.deliveryDate}>
          <div className="field-input">
            <Calendar className="input-icon" size={18} />
            <input
              className="input-field"
              type="date"
              value={data.deliveryDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                onChange('deliveryDate', selectedDate);
              }}
              min={new Date().toISOString().split('T')[0]} // Set minimum date to today
            />
          </div>
          {data.deliveryDate && (() => {
            const deliveryDate = new Date(data.deliveryDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (deliveryDate <= today) {
              return (
                <div style={{ 
                  color: ThemeColors.error.main, 
                  fontSize: Typography.fontSize.sm, 
                  marginTop: Spacing.xs 
                }}>
                  {t('forms.deliveryDateMustBeFuture')}
                </div>
              );
            }
            return null;
          })()}
        </FieldContainer>
        
        <RadioButtonGroup
          label={t('forms.orderType')}
          name="orderType"
          value={data.orderType}
          onChange={(value) => onChange('orderType', value)}
          options={[
            { label: t('forms.custOrder'), value: 'custOrder' },
            { label: t('forms.offCarDelivery'), value: 'offCarDelivery' }
          ]}
          required
          error={errors.orderType}
          showCustOrderNotice={true}
        />
        
        <FieldContainer label={t('forms.specialInstructions')}>
          <textarea
            className="input-field"
            value={data.specialInstructions}
            onChange={(e) => onChange('specialInstructions', e.target.value)}
            placeholder={t('forms.enterSpecialInstructions')}
            rows={3}
          />
        </FieldContainer>
      </OptimizedFormGrid>
    </OptimizedFormSection>
  );
};

// Enhanced FieldContainer with autopopulated styling
const EnhancedFieldContainer = styled(FieldContainer)`
  .input-field.autopopulated {
    background-color: ${ThemeColors.gray[50]};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
    border-color: ${ThemeColors.gray[200]};
    
    &:focus {
      border-color: ${ThemeColors.gray[200]};
      box-shadow: none;
    }
  }
`;

// Re-export the service function for backward compatibility
export const getMockCustomerData = CustomerDataService.getMockCustomerData;
